import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { Plus, MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const Employees = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api('/users');
        if (response.ok) {
          const data = await response.json();
          setEmployees(data);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const getRoleLabel = (role) => {
    const labels = {
      'super-admin': 'Super Admin',
      'manager': 'Manager',
      'telecaller': 'Telecaller',
      'executive': 'Sales Executive',
      'site-visit-agent': 'Site Visit Agent'
    };
    return labels[role] || role;
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const colors = [
    { bg: '#B5D4F4', text: '#0C447C' },
    { bg: '#9FE1CB', text: '#085041' },
    { bg: '#CECBF6', text: '#3C3489' },
    { bg: '#FAC775', text: '#633806' },
    { bg: '#F5C4B3', text: '#712B13' }
  ];

  const handleDelete = async (empId, empName) => {
    if (window.confirm(`Are you sure you want to delete ${empName}? This cannot be undone.`)) {
      try {
        const response = await api(`/users/${empId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          setEmployees(employees.filter(emp => emp._id !== empId));
          alert("Employee removed successfully.");
        }
      } catch (error) {
        console.error("Error deleting employee:", error);
      }
    }
  };

  return (
    <MainLayout title="Team Members">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600' }}>Team Directory</h3>
        <button 
          onClick={() => navigate('/admin/add-employee')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: 'var(--border-radius-md)',
            background: 'var(--color-brand-primary)',
            color: 'white',
            border: 'none',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Plus size={16} /> Add employee
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading team members...</div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
          gap: '16px' 
        }}>
          {employees.map((emp, idx) => {
            const color = colors[idx % colors.length];
            return (
              <div key={emp._id || idx} style={{
                background: 'var(--color-background-primary)',
                border: '1px solid var(--color-border-tertiary)',
                borderRadius: 'var(--border-radius-lg)',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                position: 'relative'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: color.bg,
                  color: color.text,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>
                  {getInitials(emp.name)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '15px' }}>{emp.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>{getRoleLabel(emp.role)}</div>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>
                    {emp.email}
                  </div>
                </div>
                {emp.role !== 'super-admin' && emp._id !== user?.id && (
                  <button 
                    onClick={() => handleDelete(emp._id, emp.name)}
                    style={{
                      padding: '6px',
                      background: 'transparent',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                    title="Delete Employee"
                  >
                    <Plus size={16} style={{ transform: 'rotate(45deg)' }} />
                  </button>
                )}
              </div>
            );
          })}
          
          <div 
            onClick={() => navigate('/admin/add-employee')}
            style={{
              border: '2px dashed var(--color-border-secondary)',
              borderRadius: 'var(--border-radius-lg)',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              color: 'var(--color-text-tertiary)',
              minHeight: '100px'
            }}
          >
            <Plus size={24} />
            <div style={{ fontSize: '13px', fontWeight: '500' }}>Add new employee</div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Employees;
